import React, {
  MouseEvent,
} from 'react';
import UserIcon from '@mui/icons-material/FolderSharedRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import noop from 'lodash/noop';
import {
  Link,
} from 'react-router-dom';
import ToolTip from 'core/components/Tooltip';
import {
  useAuth,
  useConfirmDialog,
} from 'core/hooks';
import ChevronDown from 'core/icons/chevronDown';
import Image from 'pages/Dashboard/components/Image';
import Name from 'pages/Dashboard/components/Name';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  useImpersonation,
} from 'core/context/impersonation';
import {
  APP_ROUTES,
} from 'views/EMR/router';
import {
  useUserProfilePicture,
} from 'pages/Dashboard/hooks/media';

function Menu() {
  const userInfo = useUserInfo();
  const { user, organization } = userInfo ?? {};
  const { src: profilePictureUrl } = useUserProfilePicture(user?.userId);

  const displayName = getDisplayName(
    user?.formattedNames,
    [user?.firstName, user?.lastName].filter(Boolean).join(' '),
  );
  const organizationName = organization?.name
    ?? userInfo?.organization?.displayName
    ?? '';
  const {
    confirm,
    closeConfirm,
  } = useConfirmDialog();
  const { logout } = useAuth();
  const { isImpersonating } = useImpersonation();
  const returnTo = isImpersonating ? APP_ROUTES.POPULATE_SUPPORT.ROOT : APP_ROUTES.EMR.ROOT;
  const onLogout = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    confirm({
      title: 'Logout',
      onConfirm: () => {
        closeConfirm();
        logout({ returnTo: `${window.location.origin}${returnTo}` });
      },
      content: 'Are you sure you want to logout?',
    });
  };

  return (
    <div className="flex flex-col !w-72">
      <div className="p-4 flex bg-gray-100 border-b border-b-gray-200">
        <Name
          {...(user?.formattedNames ?? {})}
          name={displayName}
          image={{
            variant: 'circular',
            size: 'md',
            src: profilePictureUrl ?? undefined,
          }}
          classes={{
            nameRoot: '!overflow-visible !whitespace-normal',
            label: 'font-semibold',
          }}
        >
          <div className="flex flex-col w-full">
            <div>{user?.email ?? '--'}</div>
            <div>
              {organizationName}
            </div>
          </div>
        </Name>
      </div>
      <Link
        to="profile"
        className="flex items-center gap-2 p-2 font-semibold cursor-pointer border-b border-b-gray-200 last:border-b-0 hover:text-blue-500 hover:bg-blue-50 nw-profile-button"
      >
        <UserIcon className="!text-blue-500 !text-xl" />
        <span>Profile</span>
      </Link>
      <div
        role="button"
        tabIndex={-1}
        onKeyDown={noop}
        onClick={onLogout}
        className="flex items-center gap-2 p-2 font-semibold cursor-pointer border-b border-b-gray-200 last:border-b-0 hover:text-blue-500 hover:bg-blue-50 nw-logout-button rounded-b-lg"
      >
        <LogoutIcon className="!text-blue-500 !text-xl" />
        <span>Log Out</span>
      </div>
    </div>
  );
}

export default function ProfileMenu() {
  const userInfo = useUserInfo();
  const { user } = userInfo ?? {};
  const { src: profilePictureUrl } = useUserProfilePicture(user?.userId);

  const displayName = getDisplayName(
    user?.formattedNames,
    [user?.firstName, user?.lastName].filter(Boolean).join(' '),
  );

  return (
    <ToolTip
      classes={{
        tooltip: '!p-0',
      }}
      title={<Menu />}
      placement="bottom-end"
      isInteractive
    >
      <div className="ml-2 flex items-center gap-1 cursor-pointer nw-dashboard-dropdown">
        <Image
          size="base"
          variant="rounded"
          className="cursor-pointer font-semibold"
          alt={displayName}
          src={profilePictureUrl ?? undefined}
        >
          {user?.formattedNames?.initials}
        </Image>
        <ChevronDown className="!w-5 !h-5 !text-gray-300" />
      </div>
    </ToolTip>
  );
}
